/**
 * Resolve chapter lockup bylines / dek from the feature WP node, with static
 * fallbacks when the node is missing (local stub) or a field is empty.
 */

export type LockupCopyFallback = {
  bylineLabel: string;
  bylineName: string;
  photoBylineLabel: string;
  photoBylineName: string;
  lede: string;
};

type AuthorLike = { readonly name?: string | null } | null | undefined;

export type LockupCopySource = {
  authors?: ReadonlyArray<AuthorLike> | null;
  additionalBylines?: string | null;
  dek?: { readonly plaintext?: string | null } | null;
} | null | undefined;

export type ResolvedLockupCopy = {
  bylineLabel: string;
  bylineName: string;
  photoBylineLabel: string;
  photoBylineName: string;
  lede: string;
};

export function resolveLockupCopy(
  source: LockupCopySource,
  fallback: LockupCopyFallback,
): ResolvedLockupCopy {
  const authorNames = (source?.authors ?? [])
    .map((author) => author?.name?.trim())
    .filter((name): name is string => Boolean(name));
  const bylineName = authorNames.length
    ? authorNames.join(", ")
    : fallback.bylineName;

  const additional = source?.additionalBylines?.trim() ?? "";
  const photoParts = additional.match(/^(.*?by\s+)(.+)$/i);
  let photoBylineLabel = fallback.photoBylineLabel;
  let photoBylineName = fallback.photoBylineName;
  if (photoParts?.[1] && photoParts[2]) {
    photoBylineLabel = photoParts[1].trimEnd();
    photoBylineName = photoParts[2].trim();
  } else if (additional) {
    photoBylineName = additional;
  }

  const lede = source?.dek?.plaintext?.trim() || fallback.lede;

  return {
    bylineLabel: fallback.bylineLabel,
    bylineName,
    photoBylineLabel,
    photoBylineName,
    lede,
  };
}
